import { navigateToUrl } from 'single-spa';
import { publishAuthSessionChanged, publishAuthUserClaimsChanged } from '@nbx/frontend-helpers/publish';
import { NBXStorage } from '@nbx/frontend-helpers/storage';
// eslint-disable-next-line import/no-cycle
import { post } from '@nbx/frontend-helpers/fetch-api';
import { getAuthBaseUrl } from './url';
import { Session } from '@nbx/frontend-helpers/types';
// eslint-disable-next-line import/no-cycle
import { Context } from './context';
import { isSessionRejected, wait, TRANSIENT_RETRY_DELAYS_MS } from './refreshFailure';

const log = (...args: unknown[]) => console.log('[helpers.auth.session]', ...args);

export class SessionTokenError extends Error {
  constructor(message = 'Cannot get session token from storage') {
    super(message);
    Object.setPrototypeOf(this, SessionTokenError.prototype);
    this.name = 'SessionTokenError';
  }
}

export const getSession = async (refresh?: boolean): Promise<Session> => {
  const rawSession = await NBXStorage.getItem('session');
  if (!rawSession) throw new SessionTokenError();
  const session = JSON.parse(rawSession);
  return refresh ? refreshSession(session) : session;
};

async function handleNewSession(responseSession: Session): Promise<void> {
  publishAuthSessionChanged(responseSession);
  const { claims } = responseSession;
  if (responseSession.claims.roles.includes('KYC')) {
    NBXStorage.removeItem(`KYC-ACCEPTED-${responseSession.userId}`, { useLocalStorage: true });
    claims.roles.filter(role => role !== 'KYC-ACCEPTED'); // TODO: ??? roles.filter is immutable
  } else {
    const kycAcceptedForUser = await NBXStorage.getItem(`KYC-ACCEPTED-${responseSession.userId}`, {
      useLocalStorage: true
    });
    if (kycAcceptedForUser) claims.roles.push('KYC-ACCEPTED');
  }
  publishAuthUserClaimsChanged(claims);
}

export async function fetchSessionFromDeviceToken(
  deviceTokenJSON: string | null | undefined = null,
  isTestAccount: boolean = false
): Promise<Session> {
  log('fetchSessionFromDeviceToken');
  if (!deviceTokenJSON)
    throw new Error(
      'Failed to fetch a new session from device token: deviceTokenFromBiometrics param is null'
    );
  const res = (await post(`${getAuthBaseUrl()}/verify/device`, {
    json: { token: JSON.parse(deviceTokenJSON).token },
    ...(isTestAccount ? { headers: { 'x-nbx-override-expire': '900' } } : {})
  })) as Session;
  if (!res) throw new Error('Failed to fetch a new session from device token: session response in empty');
  return res;
}

export async function fetchSessionFromSession(
  session: Context,
  isTestAccount: boolean = false
): Promise<Session> {
  log('fetchSessionFromSession');
  if (!session) throw new Error('Failed to fetch a new session: session param is null');
  const res = (await post(
    `${getAuthBaseUrl()}/refresh`,
    {
      ...(isTestAccount ? { headers: { 'x-nbx-override-expire': '900' } } : {})
    },
    session
  )) as Session;
  if (!res) throw new Error('Failed to fetch a new session: session response in empty');
  return res;
}

let fetchSessionTokenPromise: Session | PromiseLike<Session> | null = null;

export const clearGlobalFetchSessionTokenPromise = (): void => {
  fetchSessionTokenPromise = null;
};

export async function refreshSession(
  session: Context | null,
  deviceTokenJSON: string | null | undefined = null,
  isTestAccount: boolean = false
): Promise<Session> {
  if (fetchSessionTokenPromise) {
    // If there is an existing refresh-in-progress and we don't have a device token, piggyback on the existing
    if (!deviceTokenJSON) return fetchSessionTokenPromise;
    // If we need to start a new refresh (we have a device token), but there already is one in progress, then we wait for it finish
    // We don't care what the result is, it just needs to be done
    try {
      // eslint-disable-next-line
      console.info('Waiting for refresh-in-progress to complete before starting another with device token');
      await fetchSessionTokenPromise;
    } catch (error) {
      console.warn('Got error while waiting for refresh-in-progress, ignoring.', error);
    }
  }

  fetchSessionTokenPromise = doRefreshSession(session, deviceTokenJSON, 0, isTestAccount);
  return fetchSessionTokenPromise;
}

export const doRefreshSession = async (
  session: Context | null,
  deviceTokenJSON: string | null | undefined = null,
  attempt = 0,
  isTestAccount: boolean = false
): Promise<Session> => {
  try {
    const newSession = await (deviceTokenJSON
      ? fetchSessionFromDeviceToken(deviceTokenJSON, isTestAccount)
      : session && fetchSessionFromSession(session, isTestAccount));
    if (!newSession) throw new Error('doRefreshSession: new session is null');
    handleNewSession(newSession);
    log(`new session has been created, exp=${newSession?.exp}`);
    return newSession;
  } catch (e) {
    if (isSessionRejected(e)) {
      console.warn(
        `doRefreshSession: session rejected by the server - LOGOUT (window.PLATFORM=${window.PLATFORM} window.IS_APP_ACTIVE=${window.IS_APP_ACTIVE})`
      );
      if (window.PLATFORM === 'web') {
        window.LOGOUT();
      } else if (!window.IS_APP_ACTIVE) publishSessionExpired();
      throw e;
    }
    if (attempt < TRANSIENT_RETRY_DELAYS_MS.length) {
      log(`Session token refresh failed (will retry): ${e}`);
      await wait(TRANSIENT_RETRY_DELAYS_MS[attempt]);
      // Re-reading storage is how a null session recovers, but the biometric gate has none stored:
      // reading there would replace the real failure with a SessionTokenError.
      const sessionForRetry = deviceTokenJSON ? session : await getSession();
      return await doRefreshSession(sessionForRetry, deviceTokenJSON, attempt + 1, isTestAccount);
    }
    console.warn(`doRefreshSession: no answer from the server, session kept: ${e}`);
    throw e;
  } finally {
    clearGlobalFetchSessionTokenPromise();
  }
};

export const SESSION_LAST_ACTIVE_TIME_EPOCH = 'session:last_active_time_epoch';

let sessionTokenRefreshTimeout: ReturnType<typeof setTimeout> | null;

export const setSessionTokensRefreshTimeout = async (session: Session) => {
  // if (sessionTokenRefreshTimeout || window.PLATFORM !== 'web') return;
  if (sessionTokenRefreshTimeout) return;
  sessionTokenRefreshTimeout = setTimeout(
    async () => {
      if (new Date().getTime() / 1000 - 15 * 60 < +((await getLastActiveTime()) ?? 0)) {
        log('refresh session by getLastActiveTime timer');
        getSession()
          .then(refreshSession)
          .catch(e =>
            console.warn('setSessionTokensRefreshTimeout: cannot getSession and refreshSession', e)
          );
      }
      sessionTokenRefreshTimeout = null;
    },
    (session.claims.exp - session.claims.iat) * 1000 * 0.65
  );
};

export const setLastActiveTime = () =>
  NBXStorage.setItem(SESSION_LAST_ACTIVE_TIME_EPOCH, Math.floor(new Date().getTime() / 1000));

export const getLastActiveTime = () => NBXStorage.getItem(SESSION_LAST_ACTIVE_TIME_EPOCH);

export const publishSessionExpired = () => {
  log(`publishSessionExpired - window.location.pathname ${JSON.stringify(window.location)}`);
  if (window.location.pathname.includes('login')) {
    log('publishSessionExpired - skip (location.pathname.includes login, already published)');
    return;
  }
  log(
    `publishSessionExpired - publishAuthSessionChanged null, publishAuthUserChanged null - will be redirected to session expired`
  );
  publishAuthSessionChanged(null);
  navigateToUrl('/login/mobile-session-expired');
};
